import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppShell } from "../../layout/AppShellContext";
import { api } from "../../api/client";
import { useMenuScroll } from "../shared/useMenuScroll";
import { MenuIcon, MenuIconName } from "./MenuIcon";

type MeResponse = {
    id: number;
    phone: string | null;
    name: string | null;
    email: string | null;
    roles: string[];
    primaryRole: string;
    avatarUrl: string | null;
    companies: { id: number; name: string | null; address: string | null }[];
};

const menuItems: { label: string; href: string; icon: MenuIconName }[] = [
    { label: "Главная", href: "/", icon: "home" },
    { label: "Чаты", href: "/chats", icon: "chat" },
    { label: "Поддержка", href: "/support", icon: "tools" },
    { label: "Дизайн", href: "/order-layout", icon: "palette" },
    { label: "Документы", href: "/documents", icon: "folder" },
    { label: "Записи", href: "/records", icon: "calendar" },
    { label: "Профиль салона", href: "/company-info", icon: "salon" },
    { label: "Промокоды", href: "/promocodes", icon: "ticket" },
    { label: "Транзакции", href: "/transactions", icon: "ruble" },
    { label: "Мониторы", href: "/monitors-ads", icon: "monitor" },
    { label: "Терминал", href: "/terminal", icon: "terminal" },
    { label: "Маркировка", href: "/terminal/marking", icon: "marking" },
    { label: "Операционный день", href: "/operations-day", icon: "clock" },
    { label: "Аналитика салона", href: "/salon-analytics", icon: "analytics" },
    { label: "Планы мастеров", href: "/staff-plans", icon: "plans" },
    { label: "Мастера", href: "/staff-profiles", icon: "masters" },
    { label: "Финансы и P&L", href: "/finance", icon: "finance" },
    { label: "Эффективность команды", href: "/team-performance", icon: "performance" },
    { label: "AI-помощник", href: "/assistant", icon: "assistant" },
    { label: "Склад и закупки", href: "/inventory", icon: "inventory" },
];

type Props = { open: boolean; onClose: () => void };

const LegacyMobileMenu: React.FC<Props> = ({ open, onClose }) => {
    const [me, setMe] = useState<MeResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const listRef = useRef<HTMLUListElement>(null);
    useMenuScroll(open, listRef);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setErr(null);

        api
            .get<MeResponse>("/api/me")
            .then((res) => {
                if (!alive) {
                    return;
                }

                setMe(res);
            })
            .catch((error: any) => {
                if (!alive) {
                    return;
                }

                setErr(error?.message ?? "Ошибка загрузки профиля");
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });

        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    const initials = useMemo(() => {
        const fallback = me?.name?.trim() || me?.phone || "--";
        const parts = (me?.name || "").trim().split(/\s+/).filter(Boolean);

        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }

        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }

        return String(fallback).slice(-2).toUpperCase();
    }, [me]);

    const handleAvatarClick = () => {
        window.location.href = "/profile";
    };

    return (
        <>
            <div className={`drawer-backdrop modern${open ? " open" : ""}`} onClick={onClose} />
            <nav className={`drawer-menu modern${open ? " open" : ""}`} aria-hidden={!open}>
                <button type="button" className="drawer-close modern" aria-label="Закрыть" onClick={onClose}>
                    <svg width="38" height="38" viewBox="0 0 38 38">
                        <circle cx="19" cy="19" r="17" fill="#f7f9fa" stroke="#e72f44" strokeWidth="2" />
                        <line x1="13" y1="13" x2="25" y2="25" stroke="#e72f44" strokeWidth="2" strokeLinecap="round" />
                        <line x1="25" y1="13" x2="13" y2="25" stroke="#e72f44" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>

                <div className="drawer-user-block clickable" onClick={handleAvatarClick}>
                    {loading ? (
                        <div className="drawer-user-avatar skeleton" />
                    ) : me?.avatarUrl ? (
                        <img src={me.avatarUrl} alt={me?.name || "Профиль"} className="drawer-user-avatar" />
                    ) : (
                        <div className="drawer-user-avatar fallback">{initials}</div>
                    )}

                    <div className="drawer-user-info">
                        <span className="drawer-user-name">
                            {loading ? "Загрузка..." : me?.name || me?.phone || "Без имени"}
                        </span>
                        <span className="drawer-user-role">
                            {loading ? "" : me?.primaryRole || "Пользователь"}
                        </span>
                        {!loading && me?.companies?.length ? (
                            <span className="drawer-user-company">
                                {me.companies[0].name || me.companies[0].address || `Компания #${me.companies[0].id}`}
                            </span>
                        ) : null}
                        {err ? <span className="drawer-user-error">Ошибка: {err}</span> : null}
                    </div>
                </div>

                <ul ref={listRef}>
                    {menuItems.map((item) => (
                        <li key={item.href}>
                            <a href={item.href} onClick={onClose}>
                                <span className="menu-icon"><MenuIcon name={item.icon} /></span>
                                <span>{item.label}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
        </>
    );
};

export const MobileMenu: React.FC<Props> = (props) => useAppShell() ? null : <LegacyMobileMenu {...props} />;
export default MobileMenu;
